import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Icon, IconButton } from '@smartgift/frontend-toolkit';
import { flexRender } from '@tanstack/react-table';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import { LoaderSkeleton } from './LoaderSkeleton';
import * as S from './style';

const ActionButtons = styled.div`
  display: flex;
  gap: 4px;
  justify-content: flex-start;
`;

function DraggableRow({
  row,
  index,
  rowHeight,
  onRowClick,
  disabledRows,
  table,
  loading,
  renderSubRow
}) {
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id: row.id
  });
  const { editMode } = table.options;
  const { editing } = table.getState();
  const isEditing = editing[row.original.id];

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 'auto',
    position: 'relative',
    height: rowHeight,
    cursor: onRowClick ? 'pointer' : 'default'
  };

  const handleSave = () => {
    row.getAllCells().forEach(cell => {
      if (cell.column.columnDef.editable) {
        const input = document.querySelector(
          `[data-cell-id="${cell.id}"] input, [data-cell-id="${cell.id}"] select`
        );
        const value = input ? input.value : cell.getValue();
        table.options.meta?.updateData(row.original, cell.column.id, value, true);
      }
    });
    table.options.onEditingChange(prev => {
      const newState = { ...prev };
      delete newState[row.original.id];
      return newState;
    });
  };

  const handleCancel = () => {
    table.options.onEditingChange(prev => {
      const newState = { ...prev };
      delete newState[row.original.id];
      return newState;
    });
  };

  const handleEdit = () => {
    table.options.onEditingChange(prev => ({
      ...prev,
      [row.original.id]: true
    }));
  };

  return (
    <React.Fragment key={row.id}>
      <S.Tr
        ref={setNodeRef}
        onClick={() => {
          !!onRowClick && onRowClick(row);
        }}
        selected={row.getIsSelected()}
        disabled={disabledRows?.[row.original.id]}
        error={row.original.hasError}
        style={style}
      >
        {row.getVisibleCells().map(cell => {
          const isEditColumn = cell.column.id === 'edit';
          return (
            <S.Td
              key={cell.id}
              data-cell-id={cell.id}
              style={{ maxWidth: table.getAllLeafColumns()[index]?.getSize() || 'auto' }}
            >
              {loading ? (
                <LoaderSkeleton key={cell.id} />
              ) : isEditColumn ? (
                <ActionButtons>
                  {editMode === 'row' && isEditing ? (
                    <>
                      <IconButton variant="text" color="success" onClick={handleSave}>
                        <Icon name="success" size="lg" />
                      </IconButton>
                      <IconButton variant="text" color="error" onClick={handleCancel}>
                        <Icon name="close" size="lg" />
                      </IconButton>
                    </>
                  ) : (
                    <IconButton variant="text" color="primary" onClick={handleEdit}>
                      <Icon
                        color="var(--color-components-input-fg-default)"
                        name="edit"
                        size="lg"
                      />
                    </IconButton>
                  )}
                </ActionButtons>
              ) : (
                flexRender(cell.column.columnDef.cell, {
                  ...cell.getContext(),
                  isRowEditing: editMode === 'row' && isEditing
                })
              )}
            </S.Td>
          );
        })}
      </S.Tr>
      {row.getIsExpanded() && (
        <S.Tr>
          <S.Td colSpan={row.getAllCells().length}>{renderSubRow(row)}</S.Td>
        </S.Tr>
      )}
    </React.Fragment>
  );
}

DraggableRow.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    getIsSelected: PropTypes.func,
    getVisibleCells: PropTypes.func,
    original: PropTypes.shape({
      hasError: PropTypes.bool,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    getIsExpanded: PropTypes.func,
    getAllCells: PropTypes.func,
    renderSubRow: PropTypes.func
  }).isRequired,
  index: PropTypes.number.isRequired,
  rowHeight: PropTypes.number,
  onRowClick: PropTypes.func,
  disabledRows: PropTypes.object,
  table: PropTypes.shape({
    getAllLeafColumns: PropTypes.func,
    options: PropTypes.shape({
      editMode: PropTypes.oneOf(['inline', 'row']),
      meta: PropTypes.shape({
        updateData: PropTypes.func
      })
    })
  }).isRequired,
  loading: PropTypes.bool,
  renderSubRow: PropTypes.func
};

function TBody({ loading, dataIds, table, rowHeight, onRowClick, renderSubRow }) {
  const { disabledRows } = table.getState();
  return (
    <S.Tbody>
      <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
        {table.getRowModel().rows.map((row, index) => {
          return (
            <DraggableRow
              key={row.id}
              row={row}
              index={index}
              rowHeight={rowHeight}
              onRowClick={onRowClick}
              disabledRows={disabledRows}
              table={table}
              loading={loading}
              renderSubRow={renderSubRow}
            />
          );
        })}
      </SortableContext>
    </S.Tbody>
  );
}

TBody.propTypes = {
  loading: PropTypes.bool,
  table: PropTypes.shape({
    getState: PropTypes.func,
    getRowModel: PropTypes.func,
    getAllLeafColumns: PropTypes.func,
    options: PropTypes.shape({
      onEditingChange: PropTypes.func
    })
  }).isRequired,
  dataIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  rowHeight: PropTypes.number,
  onRowClick: PropTypes.func,
  renderSubRow: PropTypes.func
};

export { TBody };
